import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { analyticsService, useComponentImpression } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES, COMPONENT_TYPES } from '@/constants/analyticsEvents'
import { H4, Body, Caption, Modal, Button, ButtonVariant } from '@meiuca/ui-components'
import { formatDateForAPI, parseAPIDate } from '@meiuca/web-foundation'
import { ArrowLeft, CalendarX, Loader2, MapPin, Users } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { MakeupCheckoutModal } from '@/components/MakeupCheckoutModal'
import { useAuth } from '@/contexts/AuthContext'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import { formatLevel, formatTimeRange } from '@/lib/trainingCenterLabels'
import { getLevelTagClass } from '@/types/trainingCenter'
import { makeupService } from '@/services/makeupService'
import { classPackageService, type ClassPackageCatalog } from '@/services/classPackageService'
import { ClassPackagePurchaseFlow } from '@/components/ClassPackagePurchaseFlow'
import type { WorkoutListItem } from '@/services/workoutService'

interface AgendaDay {
  /** Local-midnight date of the day. */
  date: Date
  /** Stable key for grouping ("2026-08-12" in local time). */
  key: string
  dayShort: string
  dateLabel: string
  isToday: boolean
}

function localDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Janela que a tela pede. É o teto que o endpoint aceita — mais que isso é 400. */
const WINDOW_DAYS = 7

function makeAgendaDay(date: Date, todayKey: string): AgendaDay {
  const weekdayFormatter = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })
  const dateFormatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' })
  const key = localDayKey(date)

  return {
    date,
    key,
    dayShort: weekdayFormatter.format(date).replace('.', ''),
    dateLabel: dateFormatter.format(date),
    isToday: key === todayKey
  }
}

function AgendaOccurrenceCard({
  occurrence,
  position,
  onClick
}: {
  occurrence: WorkoutListItem
  position: number
  onClick: () => void
}) {
  const impressionRef = useComponentImpression({
    componentType: COMPONENT_TYPES.MAKEUP_AGENDA_OCCURRENCE_CARD,
    componentKey: `makeup_agenda_occ_${occurrence.id}`,
    position
  })
  const rawLevel = occurrence.level ?? occurrence.trainingClass?.level
  const level = formatLevel(rawLevel)
  const availableSpots =
    occurrence.maxParticipants != null
      ? occurrence.maxParticipants - occurrence.confirmedParticipantsCount
      : null
  const isFull = availableSpots != null && availableSpots <= 0
  // Quem decide se dá para entrar é o servidor — antes cada cliente refazia a
  // conta de `date + startTimeMin` e da lotação, e o checkout usava outra.
  const isAvailable = occurrence.selfEnrollment?.available ?? true
  const hasStarted = occurrence.selfEnrollment?.blockReason === 'occurrenceNotJoinable'

  return (
    <div
      ref={impressionRef}
      onClick={isAvailable ? onClick : undefined}
      onKeyDown={(e) => {
        if (!isAvailable) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      role="button"
      aria-disabled={!isAvailable}
      tabIndex={isAvailable ? 0 : -1}
      className={`rounded-lg bg-background-white border border-border-light p-4 transition-shadow focus:outline-none focus:ring-2 focus:ring-brand-primary/40 ${
        isAvailable ? 'hover:shadow-md cursor-pointer' : 'opacity-50 cursor-default'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Body className="text-base font-semibold text-text-primary mb-1">
            {formatTimeRange(occurrence.startTimeMin, occurrence.endTimeMin)}
          </Body>
          <Caption className="text-text-secondary flex items-start gap-1">
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{occurrence.location.name}</span>
          </Caption>
          {/* Quantas vagas sobraram, não a lotação da turma: "3/8" fazia o
              aluno fazer a conta e parecia que não havia vaga nenhuma. */}
          {hasStarted ? (
            <Caption className="text-text-tertiary flex items-center gap-1 mt-1">
              <Users className="w-3 h-3 flex-shrink-0" />
              Já começou
            </Caption>
          ) : (
            availableSpots != null && (
              <Caption
                className={`flex items-center gap-1 mt-1 ${
                  isFull ? 'text-text-tertiary' : 'text-emerald-700'
                }`}
              >
                <Users className="w-3 h-3 flex-shrink-0" />
                {isFull
                  ? 'Turma cheia'
                  : availableSpots === 1
                    ? '1 vaga'
                    : `${availableSpots} vagas`}
              </Caption>
            )
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {level && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelTagClass(rawLevel ?? '')}`}>
              {level}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function MakeupAgendaPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { trainingCenterId } = useParams<{ trainingCenterId: string }>()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const routeState = (location.state ?? {}) as {
    trainingCenterName?: string
    creditCount?: number
  }

  // Os dias vêm do servidor: só entram aqueles em que o aluno tem aula para
  // marcar. Oferecer sete dias fixos fazia o aluno clicar em dia vazio para
  // descobrir que não havia nada lá.
  const [availableDays, setAvailableDays] = useState<AgendaDay[]>([])
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null)
  const [occurrencesByDay, setOccurrencesByDay] = useState<Record<string, WorkoutListItem[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [checkoutOccurrence, setCheckoutOccurrence] = useState<WorkoutListItem | null>(null)
  const [packageCatalog, setPackageCatalog] = useState<ClassPackageCatalog | null>(null)
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false)

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.MAKEUP_AGENDA)
  }, [])

  useEffect(() => {
    // Show the login empty state when logged out instead of redirecting away.
    if (isAuthenticated && trainingCenterId) {
      void loadAgenda()
      // Best-effort: the buy button only appears when the CT sells packages.
      classPackageService
        .listPackagesForSale(trainingCenterId)
        .then(setPackageCatalog)
        .catch((err) => console.error('Error loading class package catalog:', err))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, trainingCenterId])

  const loadAgenda = async () => {
    if (!trainingCenterId) return
    try {
      setLoading(true)
      setError(null)

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const lastDay = new Date(today)
      lastDay.setDate(today.getDate() + WINDOW_DAYS - 1)
      lastDay.setHours(23, 59, 59, 0)

      const agenda = await makeupService.getSelfEnrollmentAgenda(trainingCenterId, {
        dateFrom: formatDateForAPI(today),
        dateTo: formatDateForAPI(lastDay)
      })

      const todayKey = localDayKey(today)
      const days: AgendaDay[] = []
      const byDay: Record<string, WorkoutListItem[]> = {}

      agenda.days.forEach((day) => {
        const date = parseAPIDate(day.date)
        date.setHours(0, 0, 0, 0)
        const agendaDay = makeAgendaDay(date, todayKey)
        days.push(agendaDay)
        byDay[agendaDay.key] = [...day.occurrences].sort((a, b) => a.startTimeMin - b.startTimeMin)
      })

      setAvailableDays(days)
      setOccurrencesByDay(byDay)
      // Mantém o dia que o aluno estava olhando; se ele saiu da agenda (a
      // última aula do dia era a que ele acabou de marcar), cai no primeiro.
      setSelectedDayKey((current) =>
        current && days.some((day) => day.key === current) ? current : (days[0]?.key ?? null)
      )
    } catch (err) {
      console.error('Error loading makeup agenda:', err)
      setError('Não foi possível carregar a agenda. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  // Troca de dia é local: a semana inteira já veio numa request.
  const selectDay = (day: AgendaDay) => {
    setSelectedDayKey(day.key)
    analyticsService.track(ANALYTICS_EVENTS.MAKEUP_AGENDA_DAY_SELECTED, {
      ct_id: trainingCenterId ?? 'unknown',
      day_offset: availableDays.findIndex((d) => d.key === day.key)
    })
  }

  const openCheckout = (occurrence: WorkoutListItem, position: number) => {
    analyticsService.track(ANALYTICS_EVENTS.SELF_ENROLL_CHECKOUT_OPENED, {
      ct_id: occurrence.trainingCenterId,
      occurrence_id: occurrence.id,
      source: 'makeup_agenda',
      position
    })
    setCheckoutOccurrence(occurrence)
  }

  const handleEnrolled = () => {
    setCheckoutOccurrence(null)
    // The student is now a participant, so the occurrence leaves the
    // notParticipating agenda on refresh — and the whole day may leave with it.
    void loadAgenda()
  }

  const currentOccurrences = selectedDayKey ? (occurrencesByDay[selectedDayKey] ?? []) : []

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate('/reposicoes')}
          className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Meus créditos
        </button>

        <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <H4 className="text-text-primary font-semibold mb-2">
              {routeState.trainingCenterName ?? 'Agenda de aulas'}
            </H4>
            <Body className="text-text-secondary">
              Escolha uma aula nos próximos 7 dias para usar seus créditos
              {routeState.creditCount != null &&
                ` — você tem ${routeState.creditCount} ${routeState.creditCount === 1 ? 'crédito' : 'créditos'}`}
            </Body>
          </div>
          {packageCatalog != null &&
            packageCatalog.pixAvailable &&
            packageCatalog.packages.length > 0 && (
              <Button
                variant={ButtonVariant.SECONDARY}
                onClick={() => setIsBuyCreditsOpen(true)}
                className="flex-shrink-0"
              >
                Comprar mais créditos
              </Button>
            )}
        </div>

        {isAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
          </div>
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para agendar sua reposição"
            description="Faça login para ver as aulas disponíveis e usar seus créditos de reposição."
          />
        ) : (
          <>
            {/* Sem dia disponível o seletor some: uma faixa vazia acima do
                estado vazio só confunde. */}
            <div
              className={`flex gap-2 mb-6 overflow-x-auto pb-2 ${
                availableDays.length === 0 ? 'hidden' : ''
              }`}
            >
              {availableDays.map((day) => {
                const isSelected = day.key === selectedDayKey
                return (
                  <button
                    key={day.key}
                    onClick={() => selectDay(day)}
                    className={`flex flex-col items-center px-4 py-2 rounded-lg border transition-colors flex-shrink-0 ${
                      isSelected
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-background-white text-text-secondary border-border-light hover:border-brand-primary/50'
                    }`}
                  >
                    <span className="text-xs font-medium capitalize">
                      {day.isToday ? 'Hoje' : day.dayShort}
                    </span>
                    <span className="text-sm font-semibold">{day.dateLabel}</span>
                  </button>
                )
              })}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
                <Body className="text-text-secondary mt-4">Carregando agenda...</Body>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Body className="text-feedback-error mb-4">{error}</Body>
                <button
                  onClick={() => void loadAgenda()}
                  className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
                >
                  Tentar novamente
                </button>
              </div>
            ) : currentOccurrences.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
                  <CalendarX className="w-8 h-8 text-text-tertiary" />
                </div>
                <H4 className="text-text-primary mb-2">Nenhuma aula disponível</H4>
                <Body className="text-text-secondary text-center max-w-md">
                  Não há aulas para agendar nos próximos {WINDOW_DAYS} dias. Fale com seu
                  professor se precisar de outra data.
                </Body>
              </div>
            ) : (
              <div className="space-y-4">
                {currentOccurrences.map((occurrence, index) => (
                  <AgendaOccurrenceCard
                    key={occurrence.id}
                    occurrence={occurrence}
                    position={index}
                    onClick={() => openCheckout(occurrence, index)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <MakeupCheckoutModal
        open={checkoutOccurrence !== null}
        occurrence={checkoutOccurrence}
        onClose={() => setCheckoutOccurrence(null)}
        onEnrolled={handleEnrolled}
      />

      {trainingCenterId && packageCatalog != null && (
        <Modal
          open={isBuyCreditsOpen}
          onClose={() => setIsBuyCreditsOpen(false)}
          title="Comprar créditos"
          size="sm"
        >
          <ClassPackagePurchaseFlow
            trainingCenterId={trainingCenterId}
            packages={packageCatalog.packages}
            source="credits"
            onClose={() => setIsBuyCreditsOpen(false)}
          />
        </Modal>
      )}
    </div>
  )
}
